<template>
	<div
		class="background"
		:style="{ backgroundImage: `url(${backgroundImage})` }"
	>
		<content-container>
			<div class="content">
				<h1 class="title">
					{{ `${locationLabelShow}'s Best Restaurants` }}
				</h1>
				<div class="subtitle">{{ subTitle }}</div>
				<div class="input-row">
					<div class="input-col">
						<input-address
							ref="address"
							:savedAddresses="savedAddresses"
							:isLoggedIn="restListHeader.inputAddress.isLoggedIn"
							:defaultAddress="defaultAddress"
							@address-set="getNewRestList"
							@noResults="showGoogleMap"
						/>
					</div>
					<div class="input-col">
						<input-restaurant-search
							:placeholder="
								'Search All ' + locationLabel + ' Restaurants'
							"
							@input="handleInputChange"
						/>
					</div>
				</div>
			</div>
		</content-container>
		<modal v-if="showMapModal" @hideModal="hideMapModal()">
			<google-map
				@addressSelected="handleMapAddressSelected"
				:mapDefaultZoom="mapDefaultZoom"
				:mapCenterLat="mapCenterLat"
				:mapCenterLng="mapCenterLng"
			></google-map>
		</modal>
	</div>
</template>

<script>
import ContentContainer from "./ContentContainer.vue";
import InputAddress from "../../address-components/InputAddress.vue";
import Modal from "../../address-components/Modal.vue";
import GoogleMap from "../../address-components/GoogleMap.vue";
import InputRestaurantSearch from "./InputRestaurantSearch.vue";
import { mapActions } from "vuex";

const debounce = require("lodash/debounce");

export default {
	name: "LocationHero",
	components: {
		ContentContainer,
		InputAddress,
		InputRestaurantSearch,
		Modal,
		GoogleMap
	},
	props: {
		locationLabel: {
			type: String,
			default: "NC"
		},
		subTitle: {
			type: String,
			default: "Brought to You by Local Delivery Heroes"
		},
		backgroundImage: {
			type: String
		},
		mapCenterLat: {
			type: String
		},
		mapCenterLng: {
			type: String
		},
		mapDefaultZoom: {
			type: String
		},
		restListHeader: {
			type: Object
		}
	},
	data: function() {
		return {
			showMapModal: false
		};
	},
	computed: {
		locationLabelShow() {
			if (!this.locationLabel) {
				return "NC";
			}
			return this.locationLabel;
		},
		savedAddresses() {
			var addresses = [];
			if (this.restListHeader.inputAddress.savedAddresses) {
				this.restListHeader.inputAddress.savedAddresses.forEach(
					address => {
						var profile = {};
						profile.id = address.id;
						var unitNum = "";
						if (address.unitNo) {
							unitNum = "Unit " + address.unitNo + ", ";
						}
						profile.address =
							address.street + ", " + unitNum + address.zip;
						profile.addressSend =
							address.street +
							" " +
							address.city +
							", " +
							address.state +
							" " +
							address.zip;
						profile.label = address.nickname;
						addresses.push(profile);
					}
				);
			}

			return addresses;
		},
		defaultAddress() {
			let defaultAddress = "";
			if (
				this.restListHeader.inputAddress.deliveryProfileId &&
				this.restListHeader.inputAddress.savedAddresses
			) {
				const deliveryProfile = this.restListHeader.inputAddress.savedAddresses.find(
					profile =>
						profile.id ===
						this.restListHeader.inputAddress.deliveryProfileId
				);
				defaultAddress =
					deliveryProfile.nickname +
					" - " +
					deliveryProfile.street +
					", " +
					deliveryProfile.zip;
			} else if (
				this.restListHeader.inputAddress.defaultAddress &&
				this.restListHeader.inputAddress.defaultCity &&
				this.restListHeader.inputAddress.defaultState &&
				this.restListHeader.inputAddress.defaultZip
			) {
				defaultAddress =
					this.restListHeader.inputAddress.defaultAddress +
					" " +
					this.restListHeader.inputAddress.defaultCity +
					", " +
					this.restListHeader.inputAddress.defaultState +
					" " +
					this.restListHeader.inputAddress.defaultZip;
			}
			return defaultAddress;
		}
	},
	methods: {
		...mapActions({
			filterRestList: "restList/filterRestList",
			goToNewRestList: "restList/goToNewRestList"
		}),
		setCookie(c_name, value, exdays) {
			var exdate = new Date();
			exdate.setDate(exdate.getDate() + exdays);
			var c_value =
				escape(value) +
				(exdays === null ? "" : "; expires=" + exdate.toUTCString()) +
				"; path=/" +
				";SameSite=Lax";
			document.cookie = c_name + "=" + c_value;
		},
		getNewRestList: debounce(
			async function(addressData) {
				const {
					latitude,
					longitude,
					address,
					streetAddress,
					city,
					state,
					zip,
					deliveryProfileId
				} = addressData;
				var result = await this.goToNewRestList({
					lat: latitude,
					lng: longitude,
					address: address
				});
				if (!result) {
					this.$refs.address.error = "";
					this.$refs.address.showLoading = false;
				} else {
					this.setCookie("address", streetAddress, 1800);
					this.setCookie("city", city, 1800);
					this.setCookie("state", state, 1800);
					this.setCookie("zip", zip, 1800);
					if (deliveryProfileId) {
						this.setCookie(
							"deliveryProfile",
							deliveryProfileId,
							1800
						);
					} else {
						this.setCookie("deliveryProfile", "", 1800);
					}
				}
			},
			2000,
			{ leading: true, trailing: false }
		),
		showGoogleMap() {
			document.body.classList.add("not-scrollable");
			this.showMapModal = true;
		},
		hideMapModal() {
			document.body.classList.remove("not-scrollable");
			this.showMapModal = false;
		},
		handleMapAddressSelected(address) {
			this.hideMapModal();
			this.$refs.address.handleAddressSelect(address);
		},
		handleInputChange(payload) {
			this.filterRestList(payload);
			this.$emit("clearSelectedFilters");
		}
	}
};
</script>

<style lang="scss" scoped>
.background {
	position: relative;
	background-color: $royal;
	background-size: cover;
	background-repeat: no-repeat;
	padding: 45px 0;

	&::after {
		content: "";
		position: absolute;
		display: block;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		background-color: rgba(0, 0, 0, 0.3);
	}
}

.content {
	position: relative;
	z-index: 1;
}

.title,
.subtitle {
	color: $white;
	font-weight: 900;
	font-size: 3.3rem;
	line-height: 1.18;
	text-align: left;
}

.subtitle {
	margin-bottom: 25px;
}

.input-row {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	margin: -10px -5px 0;
}

.input-col {
	padding: 10px 5px 0;
	width: 100%;

	@media (min-width: $mobileLandscape) {
		width: 50%;
	}
}
</style>